<template>
  <Loading v-if="loading" />
  <div class="alarms">
    <div class="alarms__header">
      <div class="alarms__header-dropdowns">
        <Dropdown
          v-if="activePickupBox"
          class="alarms__header-dropdown"
          :selected-option="selectedPickupBox"
          :options="pickupBoxOptions"
          data-testid="selectedPickupBox"
          @select-option="changePickupBox"
        />
        <div v-if="!activePickupBox" class="alarms__header-notActivePickupBox">
          <p>{{ $t('noPickupBox') }}</p>
        </div>
        <Dropdown
          class="alarms__header-dropdown"
          :selected-option="selectedAlarmCause"
          :options="alarmCauseOptions"
          :label="$t('causeOfAlarm')"
          data-testid="selectedAlarmCause"
          @select-option="changeAlarmCause"
        />
      </div>
      <div class="alarms__header-chooseDate">
        <Button
          class="getPDF-button"
          variant="bordered"
          :title="$t('downloadPDF')"
          @click="getPDF"
        />
        <div class="chooseDate-inputs">
          <div class="input">
            <Calendar
              type="date"
              format="DD/MM/YYYY"
              :value="firstDay"
              name="startTime"
              :message="$t('errorDate')"
              data-testid="calendarStartTime"
              @change="changeDateStartTime"
            />
          </div>
          <img
            class="chooseDate-img"
            src="@/assets/icons/Hyphen.svg"
            alt="Hyphen"
          />
          <div class="input">
            <Calendar
              type="date"
              format="DD/MM/YYYY"
              :value="today"
              name="endTime"
              data-testid="calendarEndTime"
              @change="changeDateEndTime"
            />
          </div>
        </div>
        <p v-if="!isDateCorrect" class="date-error">{{ $t('errorDate') }}</p>
      </div>
    </div>
    <div v-if="isDatePresent && filteredAlarmsData.length">
      <Table
        :data="filteredAlarmsData"
        :columns="columns"
        @add-alarm-comment="addAlarmComment"
      />
    </div>
    <div
      v-if="isDatePresent && !filteredAlarmsData.length"
      class="alarms__noReserve"
    >
      <p>{{ $t('noData') }}</p>
    </div>
  </div>
  <AddAlarmComment
    :show-modal="showModal"
    :active-alarm="activeAlarm"
    @close-modal="showModal = false"
    @save-alarm-comment="saveAlarmComment"
  />
</template>

<script lang="ts">
import { mapActions, mapState } from 'vuex';
import { Options, Vue } from 'vue-class-component';

import Loading from '@/views/Loading.vue';
import AddAlarmComment from './AddAlarmComment/AddAlarmComment.vue';

import { columns, alarmCauseOptions } from './configs';
import { InputFileEvent, Option } from '@/types';

import { format } from 'date-fns';
import {
  AlarmsData,
  DataAlarmPDF,
  PickupBox
} from '@/store/modules/pharmacy/types';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import {
  dateTime,
  getAlarmComments,
  getAverageTemp,
  getDuration
} from '@/utils/alarmsDownloadPDF';

@Options({
  name: 'Alarms',
  emits: ['saveAlarmComment'],
  components: {
    Loading,
    AddAlarmComment
  },
  data() {
    return {
      selectedPickupBox: {
        value: '',
        label: ''
      },
      alarmsData: [],
      pickupBoxOptions: [],
      alarmCauseOptions: alarmCauseOptions(this.$t),
      selectedAlarmCause: alarmCauseOptions(this.$t)[0],
      activePickupBox: null,
      activeAlarm: null,
      columns: columns(this.$t),
      params: null,
      loading: false,
      showModal: false
    };
  },
  created() {
    this.loadPickupBoxes();
  },
  computed: {
    ...mapState('pharmacy', ['pickupBoxes', 'alarms']),
    today() {
      const dateString = format(new Date(), 'dd/MM/yyyy');

      return dateString;
    },
    firstDay() {
      const firstDate = format(new Date(), 'dd/MM/yyyy').split('/');
      firstDate.splice(0, 1, '01');

      return firstDate.join('/');
    },
    isDateCorrect() {
      if (this.isDatePresent) {
        return this.params.endTime >= this.params.startTime;
      }

      return true;
    },
    isDatePresent() {
      return this.params && this.params.startTime && this.params.endTime;
    },
    // "All alarms" keeps the fetched list as is; any other cause narrows it
    // down to the matching alarm type.
    filteredAlarmsData() {
      const cause = this.selectedAlarmCause?.value;

      if (!cause || cause === 'all') {
        return this.alarmsData;
      }

      return this.alarmsData.filter(
        (alarm: AlarmsData) => alarm.alarmType === cause
      );
    }
  },
  methods: {
    ...mapActions('pharmacy', [
      'getPickupBoxesByPharmacy',
      'getAlarms',
      'updateAlarmComment'
    ]),
    async show() {
      this.loading = true;

      const boxId = this.activePickupBox?.id;

      const defaultParams = {
        boxId,
        ...this.params
      };

      if (
        boxId &&
        defaultParams.startTime &&
        defaultParams.endTime &&
        this.isDateCorrect
      ) {
        await this.getAlarms(defaultParams);
      }

      this.alarmsData = this.alarms;

      this.alarmsData.sort((a: AlarmsData, b: AlarmsData) => {
        return b.timestamp - a.timestamp;
      });

      this.loading = false;
    },
    initRequest() {
      let startTime = this.firstDay.split('/');
      let endTime = this.today.split('/');

      startTime = new Date(
        +startTime[2],
        startTime[1] - 1,
        +startTime[0]
      ).valueOf();
      endTime = new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf();

      this.params = {
        startTime: startTime + new Date(startTime).getTimezoneOffset() * -60000,
        endTime: endTime + new Date(endTime).getTimezoneOffset() * -60000
      };
    },
    changeDateStartTime(e: InputFileEvent) {
      const { name, value } = e.target;

      const dateNumbers = value.split('/');
      const date = `${dateNumbers[1]}/${dateNumbers[0]}/${dateNumbers[2]}`;

      this.params = {
        ...this.params,
        [name]:
          new Date(date).valueOf() + new Date(date).getTimezoneOffset() * -60000
      };
    },
    changeDateEndTime(e: InputFileEvent) {
      const { name, value } = e.target;

      const dateNumbers = value.split('/');
      const date = `${dateNumbers[1]}/${dateNumbers[0]}/${dateNumbers[2]}`;

      this.params = {
        ...this.params,
        [name]:
          new Date(date).setMonth(new Date(date).getMonth()).valueOf() -
          1000 +
          new Date(date).getTimezoneOffset() * -60000
      };
    },
    async loadPickupBoxes() {
      this.loading = true;

      const { id } = this.$route.params;

      await this.getPickupBoxesByPharmacy(id);
      this.loading = false;

      this.initPickupBoxOptions();
      this.initRequest();
    },
    initPickupBoxOptions() {
      this.pickupBoxOptions = this.pickupBoxes.map((i: PickupBox) => ({
        value: i.info.id,
        label: i.info.name,
        img: require('@/assets/icons/PickupBox.svg')
      }));
      this.selectedPickupBox = this.pickupBoxOptions[0];
      this.activePickupBox = this.pickupBoxes[0];
    },
    changePickupBox(option: Option) {
      this.selectedPickupBox = option;
      this.activePickupBox = this.pickupBoxes.find(
        (i: PickupBox) => i.info.id === option.value
      );

      this.show();
    },
    changeAlarmCause(option: Option) {
      this.selectedAlarmCause = option;
    },
    addAlarmComment(alarm: AlarmsData) {
      this.activeAlarm = alarm;

      this.showModal = true;
    },
    async saveAlarmComment(alarmInfo: string) {
      this.loading = true;

      const payload = {
        pickupboxId: this.activeAlarm.pickupboxId,
        timestamp: this.activeAlarm.timestamp,
        lockerId: this.activeAlarm.lockerId,
        alarmInfo: alarmInfo
      };

      await this.updateAlarmComment({
        data: payload
      });
      this.loading = false;

      alarmInfo = '';
      this.showModal = false;
      this.show(this.params);
    },
    getSensor(data: AlarmsData) {
      if (data.alarmType === 'temp_high' || data.alarmType === 'temp_low') {
        if (data.lockerId !== '99-99') {
          return `${this.$t('fridge')} ${data.lockerId}`;
        } else {
          return `${this.$t('roomTemp')}`;
        }
      } else if (data.lockerId) {
        return `${data.lockerId}`;
      }
    },
    getAlarmType(alarmType: string) {
      const type: Record<string, unknown> = {
        ok: this.$t('alarmOk'),
        door_open: this.$t('alarmDoorOpen'),
        door_closed: this.$t('alarmDoorClosed'),
        temp_high: this.$t('alarmTempHigh'),
        temp_low: this.$t('alarmTempLow'),
        other: this.$t('alarmOther'),
        missing: this.$t('alarmMissing')
      };

      return type[alarmType];
    },
    getPDF() {
      const data = this.filteredAlarmsData.map((el: DataAlarmPDF) => ({
        alarmType: this.getAlarmType(el.alarmType),
        timestamp: dateTime(el.timestamp),
        alarmEndTimestamp: dateTime(el.alarmEndTimestamp),
        duration: getDuration(el.timestamp, el.alarmEndTimestamp),
        alarmMax: el.alarmMax,
        alarmMin: el.alarmMin,
        sensor: this.getSensor(el),
        averageTemp: +getAverageTemp(el.alarmMax, el.alarmMin),
        alarmComment: getAlarmComments(el.check)
      }));

      const dataPDF = data.map((obj: DataAlarmPDF) => Object.values(obj));

      const columns = [...this.columns].map((i: Option) => i.label);

      const doc = new jsPDF('landscape');
      autoTable(doc, {
        head: [columns],
        body: dataPDF,
        theme: 'grid',
        headStyles: { fillColor: [71, 152, 127] },
        bodyStyles: { fillColor: [255, 255, 255] },
        margin: { top: 10 }
      });

      doc.save(`${this.$t('alarms')}.pdf`);
    }
  },
  watch: {
    params() {
      if (this.isDatePresent && this.isDateCorrect) {
        this.show(this.params);
      }
    }
  }
})
export default class Alarms extends Vue {}
</script>

<style lang="scss">
.alarms {
  padding: 17px 24px;
  .alarms__header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 23px;
    .alarms__header-dropdowns {
      display: flex;
      align-items: center;
      column-gap: 12px;
    }
    .alarms__header-dropdown {
      height: 40px;
      width: 288px !important;
    }
    .alarms__header-chooseDate {
      display: flex;
      height: 100%;
      margin-left: 10px;
      .getPDF-button {
        margin-right: 12px;
      }
      .chooseDate-inputs {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        .input {
          margin-bottom: 0;
        }
        .formkit-outer {
          height: 42px;
          margin-bottom: 0 !important;
          .formkit-wrapper {
            height: 42px;
            .formkit-inner {
              height: 42px;
            }
          }
        }
        .input {
          position: relative;
          z-index: 2;
          .input-img {
            position: absolute;
            top: 12px;
            right: 12px;
            z-index: 0;
          }
        }
        .chooseDate-img {
          margin: 0 8px;
        }
      }
      .date-error {
        color: $red;
        font-size: $fourteenSize;
        margin-top: 10px;
      }
    }
  }
  .alarms__noReserve {
    margin-top: 24px;
  }
}
</style>
